(function (Drupal, once, drupalSettings, $) {
  'use strict';

  // Shared catalogue state: filters use the Views form; shuffle stays local.
  const FORM_SELECTOR = '#views-exposed-form-catalog-page-standard';
  const VIEW_SELECTOR = '.view-id-catalog.view-display-id-page_standard';
  const STORAGE_PREFIX = 'adocs.catalog.v1.';
  let state = null;
  let confirmed = null;
  let pending = false;
  let queued = false;
  let initialized = false;
  let scrollPatched = false;
  let shuffleOrder = [];
  const originalRows = new WeakMap();

  const settings = () => drupalSettings.adocsCatalog || {};
  const filters = () => settings().filters || [];
  const defaults = () => Object.fromEntries([
    ...filters().map((key) => [key, 'All']), ['shuffle', '0'],
  ]);
  const getForm = () => document.querySelector(FORM_SELECTOR);
  const active = (value) => value && (value.shuffle === '1'
    || filters().some((key) => value[key] && value[key] !== 'All'));

  // Storage can be disabled by browser policy; filtering must still work.
  function readStored(key) {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_PREFIX + key));
    }
    catch (error) {
      return null;
    }
  }

  function store(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(STORAGE_PREFIX + key);
      else sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    }
    catch (error) { /* Persistence is optional. */ }
  }

  function setValue(form, key, value) {
    let input = form.querySelector(`[name="${key}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      form.appendChild(input);
    }
    input.value = value;
  }

  function sync(updateFilters = true) {
    if (!state) return;
    const form = getForm();
    if (form && updateFilters) {
      filters().forEach((key) => setValue(form, key, state[key]));
      form.querySelector('[name="catalog_last_clicked_filter"]')?.remove();
      form.querySelectorAll('.bef-links[data-name] a[data-bef-value]').forEach((link) => {
        const key = link.closest('.bef-links').dataset.name;
        const selected = state[key] === link.dataset.befValue;
        link.href = stateUrl(nextSelection(key, link.dataset.befValue)).toString();
        link.classList.toggle('bef-link--selected', selected);
        if (selected && link.dataset.befValue !== 'All') link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
    form?.dispatchEvent(new CustomEvent('adocs:catalog-state', { detail: state }));
    document.querySelectorAll('.catalog-filter-controls, .view-random-button').forEach((element) => {
      element.dispatchEvent(new CustomEvent('adocs:catalog-state', { detail: state }));
    });
  }

  function stateUrl(value) {
    const url = new URL(location.href);
    // Remove historical server-shuffle parameters as well as current filters.
    const owned = [...filters(), 'shuffle', 'shuffle_seed', 'catalog_last_clicked_filter'];
    [...url.searchParams.keys()].forEach((key) => {
      if (owned.includes(key.split('[')[0])) url.searchParams.delete(key);
    });
    Object.entries(value).forEach(([key, item]) => {
      if (filters().includes(key) && item !== defaults()[key]) url.searchParams.set(key, item);
    });
    return url;
  }

  function persist() {
    store('filters', settings().rememberFilters && active(confirmed) ? confirmed : null);
    store('shuffleOrder', settings().rememberFilters && confirmed?.shuffle === '1' ? shuffleOrder : null);
  }

  function validOrder(value) {
    return Array.isArray(value) ? [...new Set(value.filter((id) => typeof id === 'string' && /^[1-9][0-9]*$/.test(id)))] : [];
  }

  // Calculate IDs only: retain known ranks (including temporarily absent books)
  // and append new books in Fisher–Yates order. Neither input is modified.
  function calculateShuffleOrder(productIds, previousOrder, random = Math.random) {
    const known = new Set(previousOrder);
    const additions = productIds.filter((id) => !known.has(id));
    for (let i = additions.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [additions[i], additions[j]] = [additions[j], additions[i]];
    }
    return [...previousOrder, ...additions];
  }

  const getGrid = () => document.querySelector(`${VIEW_SELECTOR} .catalog-teasers`);

  function applyOrder() {
    const grid = getGrid();
    if (!grid || !state) return;
    const ids = new Map([...grid.children].map((row) => [row, row.querySelector('[data-product-id]')?.dataset.productId]));
    const id = (row) => ids.get(row);
    const rows = [...grid.children].filter((row) => id(row));
    if (!originalRows.has(grid)) originalRows.set(grid, rows);
    let ordered = originalRows.get(grid);
    if (state.shuffle === '1') {
      shuffleOrder = calculateShuffleOrder(rows.map(id), shuffleOrder);
      // Translate the calculated ID ranks into existing DOM nodes.
      const rank = new Map(shuffleOrder.map((key, index) => [key, index]));
      ordered = [...rows].sort((a, b) => rank.get(id(a)) - rank.get(id(b)));
    }
    if (ordered.some((row, index) => row !== rows[index])) {
      // Move existing nodes without rerendering or reattaching Drupal behaviors.
      // Keep invisible grid fillers at the end, outside the shuffled books.
      const filler = [...grid.children].find((row) => row.classList.contains('grid-filler')) || null;
      const fragment = document.createDocumentFragment();
      ordered.forEach((row) => fragment.appendChild(row));
      grid.insertBefore(fragment, filler);
    }
  }

  function sameFilters(first, second) {
    return filters().every((key) => first[key] === second[key]);
  }

  function submit() {
    const form = getForm();
    if (!form) return;
    sync();
    const submitButton = form.querySelector('.form-submit:not([data-drupal-selector*="edit-reset"])');
    if (!submitButton) return;
    pending = true;
    // This also works when the filter panel is collapsed/inert.
    submitButton.click();
  }

  function change(next) {
    if (!state) return;
    state = { ...defaults(), ...next };
    sync();
    if (pending) queued = true;
    else if (sameFilters(state, confirmed)) {
      confirmed = { ...state };
      applyOrder();
      persist();
    }
    else submit();
  }

  function nextSelection(key, value) {
    const next = { ...state };
    next[key] = state[key] === value ? 'All' : value;
    // A new selection wins; deselecting never changes the other filter.
    if (settings().exclusiveTopicSeries && next[key] !== 'All') {
      if (key === 'topic') next.series = 'All';
      if (key === 'series') next.topic = 'All';
    }
    return next;
  }

  function select(key, value) {
    if (filters().includes(key) && state) change(nextSelection(key, value));
  }

  function onFilterClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a.bef-link[data-bef-value]');
    const key = link?.closest('.bef-links[data-name]')?.dataset.name;
    if (!filters().includes(key)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    select(key, link.dataset.befValue);
    // The handled click does not bubble; notify the panel after applying it.
    event.currentTarget.dispatchEvent(new CustomEvent('adocs:catalog-select', { bubbles: true }));
  }

  function isCatalogRequest(options) {
    const url = new URL(options.url, location.href);
    return url.searchParams.get('view_name') === 'catalog'
      && url.searchParams.get('view_display_id') === 'page_standard';
  }

  Drupal.adocsCatalog = {
    settings, getForm, active, readStored, store, select,
    getState: () => state,
    // Snapshot the rendered results, including neutral filters. Explicit values
    // prevent a later catalogue visit from changing a book's return destination.
    getReturnUrl() {
      const url = stateUrl(confirmed || defaults());
      filters().forEach((key) => url.searchParams.set(key, (confirmed || defaults())[key]));
      return url.href;
    },
    getShuffleState: () => ({ active: confirmed?.shuffle === '1', order: [...shuffleOrder] }),
    reset() {
      shuffleOrder = [];
      change(defaults());
    },
    shuffle() {
      if (!state) return;
      state.shuffle = '1';
      confirmed.shuffle = '1';
      shuffleOrder = [];
      // Notify controls before moving cards: their CSS-mode reads must not
      // synchronously flush layout for the entire reordered grid.
      sync(false);
      applyOrder();
      persist();
    },
  };

  Drupal.AjaxCommands.prototype.adocsCatalogState = function (ajax, response) {
    confirmed = { ...defaults(), ...response.state, shuffle: state.shuffle };
    if (!queued) {
      state = { ...confirmed };
      sync();
    }
    applyOrder();
    persist();
    document.body.classList.remove('catalog-restoring');
  };

  Drupal.behaviors.catalogState = {
    attach() {
      const form = getForm();
      if (!form || !filters().length) return;
      if (!scrollPatched) {
        const original = Drupal.AjaxCommands.prototype.scrollTop;
        Drupal.AjaxCommands.prototype.scrollTop = function (ajax, response) {
          if (ajax?.submit?.view_name === 'catalog' && ajax.submit.view_display_id === 'page_standard') return;
          return original.call(this, ajax, response);
        };
        scrollPatched = true;
      }
      once('catalog-state-form', [form]).forEach((element) => {
        element.addEventListener('click', onFilterClick, true);
      });
      if (!initialized) {
        initialized = true;
        state = { ...defaults(), ...JSON.parse(form.dataset.catalogState) };
        const returning = Drupal.adocsCatalogReturn.getInitialState()?.catalogShuffle;
        if (returning?.active === true) {
          state.shuffle = '1';
          shuffleOrder = validOrder(returning.order);
        }
        confirmed = { ...state };
        const url = new URL(location.href);
        // Old shuffle links are explicit visits too, never stored-filter restores.
        const explicit = [...url.searchParams.keys()].some((key) =>
          [...filters(), 'shuffle', 'shuffle_seed'].includes(key.split('[')[0]));
        const stored = settings().rememberFilters ? readStored('filters') : null;
        if (!returning && !explicit && stored?.shuffle === '1') {
          shuffleOrder = validOrder(readStored('shuffleOrder'));
        }
        $(document).on('ajaxComplete.adocsCatalog', (event, xhr, options) => {
          if (!pending || !isCatalogRequest(options)) return;
          pending = false;
          document.body.classList.remove('catalog-restoring');
          if (queued) {
            queued = false;
            submit();
          }
          else {
            state = { ...confirmed };
            sync();
          }
        });
        if (!returning && !explicit && stored && typeof stored === 'object' && active(stored)) {
          // Only restore allowlisted fields, then let the server validate values.
          const restored = defaults();
          Object.keys(restored).forEach((key) => {
            if (typeof stored[key] === 'string') restored[key] = stored[key];
          });
          if (!sameFilters(restored, confirmed)) document.body.classList.add('catalog-restoring');
          requestAnimationFrame(() => change(restored));
        }
        else {
          Drupal.adocsCatalogReturn.normalizeUrl(stateUrl(state));
          persist();
        }
        if (!settings().rememberPanelState) {
          store('expanded', null);
          store('mobileGroup', null);
        }
      }
      applyOrder();
      sync();
    },
  };
})(Drupal, once, drupalSettings, jQuery);
